import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useMergeRefs,
} from "@floating-ui/react"

import classNames from "classnames"

import type {
  Ref,
  ComponentPropsWithoutRef,
  CSSProperties,
} from "react"
import {
  forwardRef,
  useMemo,
} from "react"

import styles from "./Modal.module.scss"
import { useModalContext } from "./useModalContext"
import { Form } from "src/inputs/Form"

type Props = ComponentPropsWithoutRef<"form">

// this is the same as ModalContent but renders a form element
export const ModalContentForm = forwardRef<HTMLFormElement, Props>(
  function ModalContentForm(props: Props, propRef: Ref<HTMLFormElement>) {
    const state = useModalContext()

    const ref = useMergeRefs([
      state.refs.setFloating,
      propRef,
    ])

    const {
      className: floatingClassName,
      ...floatingProperties
    }: Props = state.getFloatingProps(props)

    const className = classNames(
      styles.modalContent,
      floatingClassName,
    )

    const style: CSSProperties = useMemo(
      () => ({
        transitionDuration: `${state.transitionMs}ms`,
      }),
      [state.transitionMs],
    )

    return (
      <FloatingPortal>
        {state.isMounted && (
          <FloatingOverlay
            className={styles.modalOverlay}
            data-status={state.status}
            style={style}
            lockScroll
          >
            <FloatingFocusManager context={state.context}>
              <Form
                ref={ref}
                aria-labelledby={state.labelId}
                aria-describedby={state.descriptionId}
                {...floatingProperties}
                className={className}
                data-status={state.status}
                style={{
                  ...floatingProperties.style,
                  ...style,
                }}
              />
            </FloatingFocusManager>
          </FloatingOverlay>
        )}
      </FloatingPortal>
    )
  },
)
